import { Building2, Map, Radio, ScrollText, Lock, ArrowLeftRight } from 'lucide-react';
import { Hero, SectionHeader, FeatureGrid, Slab, MockVisual, CTABanner } from '@/components/marketing/primitives.jsx';

const features = [
  {
    icon: Map,
    title: 'Live dispatch board',
    description:
      'Map + queue for every open report in your jurisdiction. Accept tasks, update status, coordinate units, and hand off cleanly.',
  },
  {
    icon: Radio,
    title: 'BOLO + alerts',
    description:
      'Publish BOLO records that propagate across participating agencies in near-real-time. Every record has expiry, revocation, and signed provenance.',
  },
  {
    icon: ScrollText,
    title: 'Chain-of-custody',
    description:
      'Every submission arrives with device attestation, hashed media, and an integrity chain you can inspect before taking action.',
  },
  {
    icon: ArrowLeftRight,
    title: 'Cross-agency handoff',
    description:
      'Routing manifests preserve who had custody, for how long, and what decisions were made — across jurisdictions.',
  },
  {
    icon: Lock,
    title: 'Tenant isolation',
    description:
      'Agency scope is enforced at the controller, the query, and the WebSocket subscription. Cross-tenant access is structurally impossible.',
  },
  {
    icon: Building2,
    title: 'Inspector General read access',
    description:
      'Oversight is built in, not bolted on. IG roles have standing read access across audit and routing logs.',
  },
];

export default function ForAgencies() {
  return (
    <>
      <Hero
        eyebrow="For Agencies"
        title="One pane of glass for reports, dispatch, and BOLO — with audit that holds up."
        description="NACIS gives state and local law enforcement — and participating federal agencies — a shared, integrity-preserving surface for civic reporting, dispatch coordination, and cross-agency handoff."
        primaryCta={{ to: '/contact', label: 'Request onboarding' }}
        secondaryCta={{ to: '/subsystems/rerr', label: 'See RERR in depth' }}
      />

      <section className="py-20 md:py-24">
        <div className="container-wide">
          <SectionHeader
            eyebrow="What you get on day one"
            title="The operational surface, fully integrated."
          />
          <div className="mt-12">
            <FeatureGrid items={features} columns={3} />
          </div>
        </div>
      </section>

      <Slab
        eyebrow="Integration surface"
        title="Bring your CAD, RMS, and alert systems."
        description="NACIS is built to sit alongside your existing Computer-Aided Dispatch and Records Management Systems. Partners can consume events through our API, our WebSocket channels, or both — with role and jurisdiction enforcement on every call."
        bullets={[
          'REST API with per-agency API tokens via Laravel Sanctum',
          'Real-time events over Pusher-protocol private channels',
          'Warrant packaging and judicial routing through the Judicial layer',
          'BOLO publish/subscribe with signed provenance and revocation',
        ]}
        image={<MockVisual accent="sky" lines={6} />}
      />

      <Slab
        reverse
        eyebrow="Onboarding path"
        title="Observer → sandbox → production."
        description="Every agency onboards through a tiered path. You start in observer mode against the Interoperability Sandbox with synthetic traffic, progress through supervised integration, and only go live on production with Safe Entity Attestation completed."
        bullets={[
          'Observer — read-only access to sandbox, no production data',
          'Integration — sandbox mutations with your real systems wired in',
          'Federated — production access with attested credentials and IG oversight',
        ]}
        image={<MockVisual accent="emerald" lines={5} />}
      />

      <CTABanner
        title="Start your agency onboarding."
        description="Typical observer-to-federated timeline is 6–10 weeks with dedicated technical support. Longer for agencies with complex RMS integrations."
        primaryCta={{ to: '/contact', label: 'Talk to onboarding' }}
        secondaryCta={{ to: '/security', label: 'Security model' }}
      />
    </>
  );
}
